import { useEffect, useState } from "react";
import {
  Building2, Target, Calendar, TrendingUp, Eye, Users2, CheckCircle2, Home, Loader2,
} from "lucide-react";
import {
  BarChart, Bar, PieChart, Pie, Cell, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid,
} from "recharts";
import api from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { useLang } from "../../context/LanguageContext";
import { formatPrice, img } from "../../lib/utils-app";

const GOLD = "#1F9AA8";
const COLORS = ["#1F9AA8", "#F2795A", "#4FB0BE", "#F4B36B", "#7FD1C3"];

function KPI({ icon: Icon, label, value, sub }) {
  return (
    <div className="bg-card border border-border p-6" data-testid={`kpi-${label}`}>
      <div className="flex items-center justify-between mb-4">
        <Icon size={20} className="text-primary" strokeWidth={1.4} />
        {sub && <span className="text-xs text-emerald-400">{sub}</span>}
      </div>
      <p className="font-serif text-4xl">{value}</p>
      <p className="label-caps text-muted-foreground text-[0.6rem] mt-2">{label}</p>
    </div>
  );
}

export function Overview({ role }) {
  const { user } = useAuth();
  const { t, L, lang } = useLang();
  const [stats, setStats] = useState(null);
  const [favCount, setFavCount] = useState(0);
  const [apptCount, setApptCount] = useState(0);

  const isStaff = role === "admin" || role === "agent";

  useEffect(() => {
    if (isStaff) api.get("/admin/stats").then((r) => setStats(r.data));
    else {
      api.get("/favorites").then((r) => setFavCount(r.data.items.length));
      api.get("/appointments").then((r) => setApptCount(r.data.items.length));
    }
  }, [isStaff]);

  if (!isStaff) {
    return (
      <div>
        <h1 className="font-serif text-4xl font-light mb-2">{t("dash.welcome")}, {user.name.split(" ")[0]}</h1>
        <p className="text-muted-foreground font-light mb-8">Your personal real estate hub.</p>
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
          <KPI icon={Home} label={t("dash.favorites")} value={favCount} />
          <KPI icon={Calendar} label={t("dash.appointments")} value={apptCount} />
          <KPI icon={Eye} label="Member Since" value="2026" />
        </div>
        <div className="mt-8 bg-card border border-border p-8">
          <h2 className="font-serif text-2xl mb-3">Discover more homes</h2>
          <p className="text-muted-foreground font-light mb-5">Browse our curated collection and save your favorites.</p>
          <a href="/buy" className="inline-block px-6 py-3 bg-primary text-primary-foreground label-caps hover:brightness-110 transition-[filter]" data-testid="browse-homes">{t("nav.buy")}</a>
        </div>
      </div>
    );
  }

  if (!stats) return <div className="grid place-items-center py-40"><Loader2 className="animate-spin text-primary" size={32} /></div>;

  const sourceData = (stats.leads_by_source || []).map((s) => ({ name: s._id || "other", value: s.count }));
  const stageData = (stats.leads_by_stage || []).map((s) => ({ name: s._id || "new", value: s.count }));

  return (
    <div>
      <h1 className="font-serif text-4xl font-light mb-2">{t("dash.welcome")}, {user.name.split(" ")[0]}</h1>
      <p className="text-muted-foreground font-light mb-8">{role === "admin" ? "Full platform overview." : "Your portfolio at a glance."}</p>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
        <KPI icon={Building2} label={t("dash.activeListings")} value={stats.active_listings} />
        <KPI icon={Target} label={t("dash.newLeads")} value={stats.new_leads} />
        <KPI icon={Calendar} label={t("dash.upcoming")} value={stats.upcoming_appointments} />
        <KPI icon={TrendingUp} label={t("dash.conversion")} value={`${stats.conversion_rate}%`} />
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <KPI icon={Eye} label={t("dash.totalViews")} value={stats.total_views.toLocaleString()} />
        <KPI icon={CheckCircle2} label={t("dash.sold")} value={stats.sold} />
        <KPI icon={Target} label="Total Leads" value={stats.total_leads} />
        {role === "admin" && <KPI icon={Users2} label={t("dash.totalUsers")} value={stats.total_users} />}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-8">
        <div className="bg-card border border-border p-6">
          <h3 className="font-serif text-2xl mb-6">{t("dash.leadsBySource")}</h3>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={sourceData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E4E7EC" vertical={false} />
              <XAxis dataKey="name" stroke="#5B6472" fontSize={12} tickLine={false} />
              <YAxis stroke="#5B6472" fontSize={12} tickLine={false} axisLine={false} />
              <Tooltip contentStyle={{ background: "#ffffff", border: "1px solid #E4E7EC", borderRadius: 0 }} />
              <Bar dataKey="value" fill={GOLD} radius={[2, 2, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="bg-card border border-border p-6">
          <h3 className="font-serif text-2xl mb-6">{t("dash.leadsByStage")}</h3>
          <ResponsiveContainer width="100%" height={240}>
            <PieChart>
              <Pie data={stageData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={90} label={(e) => e.name}>
                {stageData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
              </Pie>
              <Tooltip contentStyle={{ background: "#ffffff", border: "1px solid #E4E7EC", borderRadius: 0 }} />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-card border border-border p-6">
          <h3 className="font-serif text-2xl mb-4">{t("dash.topProperties")}</h3>
          <div className="space-y-3">
            {stats.top_properties?.map((p) => (
              <div key={p.id} className="flex items-center gap-3">
                <img src={img(p.images?.[0])} alt="" className="w-14 h-14 object-cover" />
                <div className="min-w-0 flex-1">
                  <p className="font-light truncate">{L(p.title)}</p>
                  <p className="text-xs text-muted-foreground">{p.city} · {formatPrice(p.price)}</p>
                </div>
                <span className="flex items-center gap-1 text-sm text-primary"><Eye size={14} /> {p.views}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="bg-card border border-border p-6">
          <h3 className="font-serif text-2xl mb-4">{t("dash.recentActivity")}</h3>
          <div className="space-y-3">
            {stats.recent_leads?.map((l, i) => (
              <div key={i} className="flex items-center gap-3 py-2 border-b border-border last:border-0">
                <span className="w-8 h-8 rounded-full bg-primary/15 text-primary grid place-items-center text-xs font-medium">{l.name?.[0]}</span>
                <div className="min-w-0 flex-1">
                  <p className="font-light text-sm truncate">{l.name}</p>
                  <p className="text-xs text-muted-foreground truncate">{l.source} · {l.stage}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
